// Noticing a new deploy and applying it without asking.
//
// This page is the kind of thing you leave open in a tab and come back to in
// November. Without this, that tab keeps showing whatever the rotation said
// the day it was opened - a deploy that fixes a host or adds a year never
// reaches it. Ported from Cinema Roll, where Matt's call was that the user
// shouldn't have to take an action for an update they didn't ask about.
//
// Detection compares the hashed entry bundle the server is serving against the
// one this page actually loaded, so it doesn't depend on any lifecycle event
// or cache header being right. Every failure here is silent: a missed check
// just means we try again on the next trigger.

// The webpack entry chunk, hashed per build: `js/app.ab809800.js`. Verified
// against this repo's own `yarn build` output - the vendor chunk and the CSS
// bundle are named differently, so this can only match the app entry.
const BUNDLE_PATTERN = /js\/app\.[a-z0-9]+\.js/

/** The entry bundle named in a served index.html, or null. */
export const bundleNameIn = (html) => {
  const match = String(html || '').match(BUNDLE_PATTERN)
  return match ? match[0] : null
}

/** The entry bundle THIS page is running, read off its own script tags. */
export const runningBundleName = (scripts = document.querySelectorAll('script[src]')) => {
  for (const script of Array.from(scripts)) {
    const match = (script.getAttribute('src') || '').match(BUNDLE_PATTERN)
    if (match) return match[0]
  }
  return null
}

/**
 * Fetches index.html and reports the deployed bundle name if it differs from
 * the running one, otherwise null.
 *
 * The cache-busting param is the point of the whole request: without it a
 * CloudFront or browser copy of index.html can answer, which is exactly the
 * stale answer we're trying to see past. `cache: 'no-store'` on top of it.
 */
export const fetchDeployedBundle = async (runningBundle) => {
  if (!runningBundle) return null

  try {
    const base = (typeof process !== 'undefined' && process.env && process.env.BASE_URL) || '/'
    const response = await fetch(`${base}index.html?updateCheck=${Date.now()}`, { cache: 'no-store' })
    if (!response.ok) return null

    const deployed = bundleNameIn(await response.text())
    return deployed && deployed !== runningBundle ? deployed : null
  } catch {
    // Offline, blocked, or the request simply failed - try again next time.
    return null
  }
}

// No service worker here (this site has none), so there is nothing to wait
// for. Kept anyway so the update path is the same shape in every app: the day
// one of them registers a worker, a reload has to land on the NEW app rather
// than a half-swapped one, and that's what this cap is for.
export async function waitForNewWorker (timeoutMs = 15000) {
  try {
    const registration = await navigator.serviceWorker?.getRegistration?.()
    if (!registration) return
    await registration.update().catch(() => {})
    const deadline = Date.now() + timeoutMs
    while ((registration.installing || registration.waiting) && Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 250))
    }
  } catch {
    // Any surprise here must never eat the reload itself.
  }
}

export async function reloadForUpdate () {
  await waitForNewWorker()
  window.location.reload()
}

// Is RIGHT NOW a safe moment to reload out from under the reader? This page
// has no text entry at all today - it's cards, a toggle and a download button,
// none of which hold state worth losing - so this only ever returns false in
// theory. It stays because the check is cheap and the page won't stay
// input-free forever; the day a form lands, the guard is already here.
export function isSafeMomentForReload ({ activeElement = document.activeElement } = {}) {
  const tag = activeElement?.tagName || ''
  if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag)) return false
  return true
}

// One auto-attempt per detected target bundle, ever. If a reload somehow
// doesn't get us onto the new version, the page settles on the old one rather
// than reloading in a loop - a loop is far worse than a stale tab.
const ATTEMPT_KEY = 'auto-update-attempted-for'

export function shouldAutoAttempt (targetBundle, storage = window.sessionStorage) {
  try {
    if (storage.getItem(ATTEMPT_KEY) === targetBundle) return false
    storage.setItem(ATTEMPT_KEY, targetBundle)
    return true
  } catch {
    return true // storage unavailable: still better to try once than never
  }
}
